/**
 * One journey, reduced to what the map needs: whose it was and when it ran.
 *
 * Positions are read for whole days, so the interval is what separates the journeys that were
 * picked from the vehicle's other journeys on the same day.
 */
export interface TripRange {
  tripId: string;
  deviceId: string;
  start: Date;
  stop: Date;
}

/** A position, as the map wants it. */
export interface RoutePoint {
  deviceId: string;
  latitude: number;
  longitude: number;
  time: number;
  /** Kilometres per hour, as reported with the position. */
  speed: number;
}

/** A stretch between two positions, and the colour to draw it in. */
export interface RouteSegment {
  deviceId: string;
  from: RoutePoint;
  to: RoutePoint;
  /** The average of the two ends, which is what the colour is chosen from. */
  speed: number;
}

/**
 * The colours a speed is drawn in, slowest to fastest.
 *
 * Indigo through to red: a scale a reader can order at a glance without a legend, though there is
 * one anyway. Seven steps, because a smooth gradient over a thin line reads as one muddy colour.
 */
export const SPEED_COLORS = [
  "#3f51b5",
  "#2196f3",
  "#00bcd4",
  "#4caf50",
  "#ffeb3b",
  "#ff9800",
  "#f44336",
];

/**
 * The colours vehicles are drawn in when speed is not the subject.
 *
 * Ten, and handed out in the order the vehicles first appear, so a vehicle keeps its colour
 * across the line, its markers and the legend.
 */
export const VEHICLE_COLORS = [
  "#1e88e5",
  "#e53935",
  "#43a047",
  "#fb8c00",
  "#8e24aa",
  "#00897b",
  "#3949ab",
  "#d81b60",
  "#6d4c41",
  "#546e7a",
];

/** What a position needs to have before it can be drawn. */
interface RawLogRecord {
  device?: { id?: string } | null;
  dateTime?: Date | string | null;
  latitude?: number | null;
  longitude?: number | null
  speed?: number | null;
}

/**
 * The positions worth drawing, in time order.
 *
 * Two filters matter. A position without a usable fix would put the line in the Atlantic. And a
 * position outside every picked journey belongs to a journey the reader did not pick — the
 * positions were read by the day, so the vehicle's other trips that day come along with them.
 *
 * @param logRecords - What Geotab returned, in any order.
 * @param tripRanges - The journeys that were picked. Empty means keep everything.
 */
export function toRoutePoints(
  logRecords: readonly RawLogRecord[],
  tripRanges: readonly TripRange[],
): RoutePoint[] {
  const rangesByDevice = new Map<string, TripRange[]>();
  for (const range of tripRanges) {
    const existing = rangesByDevice.get(range.deviceId);
    if (existing) {
      existing.push(range);
    } else {
      rangesByDevice.set(range.deviceId, [range]);
    }
  }

  const points = logRecords.flatMap((record): RoutePoint[] => {
    const deviceId = record.device?.id;
    const latitude = record.latitude;
    const longitude = record.longitude;

    if (!deviceId || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return [];
    }

    const time = new Date(record.dateTime ?? 0).getTime();
    if (!Number.isFinite(time)) {
      return [];
    }

    if (tripRanges.length > 0) {
      const ranges = rangesByDevice.get(deviceId);
      const insidePickedTrip = ranges?.some(
        range => time >= range.start.getTime() && time <= range.stop.getTime(),
      );
      if (!insidePickedTrip) {
        return [];
      }
    }

    return [
      {
        deviceId,
        latitude: latitude as number,
        longitude: longitude as number,
        time,
        speed: Number.isFinite(record.speed) ? (record.speed as number) : 0,
      },
    ];
  });

  return points.sort((first, second) => first.time - second.time);
}

/**
 * The stretches between the positions.
 *
 * Only ever between two positions of the same vehicle. Joining across vehicles would draw a line
 * from one lorry to another, which looks exactly like a journey and is not one.
 */
export function toRouteSegments(points: readonly RoutePoint[]): RouteSegment[] {
  const byDevice = new Map<string, RoutePoint[]>();
  for (const point of points) {
    const existing = byDevice.get(point.deviceId);
    if (existing) {
      existing.push(point);
    } else {
      byDevice.set(point.deviceId, [point]);
    }
  }

  const segments: RouteSegment[] = [];
  for (const [deviceId, devicePoints] of byDevice) {
    for (let index = 1; index < devicePoints.length; index++) {
      const from = devicePoints[index - 1];
      const to = devicePoints[index];
      segments.push({ deviceId, from, to, speed: (from.speed + to.speed) / 2 });
    }
  }

  return segments;
}

/** The slowest and fastest speed on the route, for the colour scale and its legend. */
export function speedRange(points: readonly RoutePoint[]): { min: number; max: number } {
  if (points.length === 0) {
    return { min: 0, max: 0 };
  }

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    min = Math.min(min, point.speed);
    max = Math.max(max, point.speed);
  }
  return { min, max };
}

/**
 * The colour for a speed, relative to the rest of this route.
 *
 * Relative rather than against fixed thresholds: a delivery round that never passes 50 would be
 * one colour on an absolute scale, and the whole point is to see where it slowed down.
 */
export function speedColor(speed: number, min: number, max: number): string {
  if (!(max > min)) {
    // Everything at one speed, or a single point: no gradient to place it on.
    return SPEED_COLORS[Math.floor(SPEED_COLORS.length / 2)];
  }

  const share = Math.min(1, Math.max(0, (speed - min) / (max - min)));
  return SPEED_COLORS[Math.round(share * (SPEED_COLORS.length - 1))];
}

/** The vehicles in the order they first appear, which is the order colours are handed out in. */
export function deviceOrder(points: readonly RoutePoint[]): string[] {
  const seen: string[] = [];
  for (const point of points) {
    if (!seen.includes(point.deviceId)) {
      seen.push(point.deviceId);
    }
  }
  return seen;
}

export function vehicleColor(deviceId: string, order: readonly string[]): string {
  const index = order.indexOf(deviceId);
  return VEHICLE_COLORS[(index < 0 ? 0 : index) % VEHICLE_COLORS.length];
}

/** A journey's ends, numbered so a reader can tell the third trip from the fourth. */
export interface TripMarker extends TripRange {
  /** Counted per vehicle, so each vehicle's journeys start again at one. */
  number: number;
}

/**
 * The journeys in time order, numbered per vehicle.
 *
 * Numbering across all of them would say "trip 7" on a vehicle showing only two.
 */
export function toTripMarkers(tripRanges: readonly TripRange[]): TripMarker[] {
  const counted = new Map<string, number>();

  return [...tripRanges]
    .sort((first, second) => first.start.getTime() - second.start.getTime())
    .map(range => {
      const next = (counted.get(range.deviceId) ?? 0) + 1;
      counted.set(range.deviceId, next);
      return { ...range, number: next };
    });
}

/**
 * The window of positions to read for each vehicle.
 *
 * One window per vehicle. With one vehicle's journey on Monday and another's on Friday, a shared
 * window would read five days of positions for both — which is what made this page slow.
 *
 * Widened to whole days at both ends, so a journey that runs over midnight is not cut in half by
 * the read itself.
 *
 * @param tripRanges - The journeys that were picked.
 */
export function toDeviceRanges(
  tripRanges: readonly TripRange[],
): { deviceId: string; fromDate: Date; toDate: Date }[] {
  const windows = new Map<string, { from: number; to: number }>();

  for (const range of tripRanges) {
    const start = startOfDay(range.start);
    const stop = endOfDay(range.stop);
    const existing = windows.get(range.deviceId);

    windows.set(range.deviceId, {
      from: existing ? Math.min(existing.from, start) : start,
      to: existing ? Math.max(existing.to, stop) : stop,
    });
  }

  return [...windows.entries()]
    .map(([deviceId, window]) => ({
      deviceId,
      fromDate: new Date(window.from),
      toDate: new Date(window.to),
    }))
    .sort((first, second) => first.deviceId.localeCompare(second.deviceId));
}

function startOfDay(date: Date): number {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy.getTime();
}

function endOfDay(date: Date): number {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy.getTime();
}

/** The two corners a map has to show to have every point in frame, west-south first. */
export type RouteBounds = [[number, number], [number, number]];

/**
 * The corners of the driven route.
 *
 * `null` rather than a degenerate box when there is nothing to frame: a map asked to fit an empty
 * area picks its own answer, and it is never a useful one.
 */
export function routeBounds(points: readonly RoutePoint[]): RouteBounds | null {
  if (points.length === 0) {
    return null;
  }

  let west = points[0].longitude;
  let east = points[0].longitude;
  let south = points[0].latitude;
  let north = points[0].latitude;

  for (const point of points) {
    west = Math.min(west, point.longitude);
    east = Math.max(east, point.longitude);
    south = Math.min(south, point.latitude);
    north = Math.max(north, point.latitude);
  }

  return [
    [west, south],
    [east, north],
  ];
}

/** The two ends of a piece of the route, in the order a map wants them. */
export function toSegmentPath(segment: RouteSegment): [number, number][] {
  return [
    [segment.from.longitude, segment.from.latitude],
    [segment.to.longitude, segment.to.latitude],
  ];
}
